/**
 * 吃饭睡觉补贴活动 - 日历提醒
 *
 * 写入：使用 addRecurringCalendarEvents 创建未来 30 天的每日提醒
 * 删除：使用 deleteEventsByTitle 按 title 批量删除
 */

import dayjs from 'dayjs';
import Toast from '@/components/toast';
import { env } from '@hb/NBridge/lib/h5';
import { getVersion } from '@/utils/systemCode';
import { compareVersions } from '@/utils/tools';
import {
  addRecurringCalendarEvents,
  deleteEventsByTitle,
} from './calendar-bridge';
import { CALENDAR_REMIND, TEXT } from './constants';

const REMIND_DAYS = 30;
const MIN_VERSION = '6.99.50';

/**
 * 根据 "HH:mm:ss" 生成今天对应的秒级时间戳（作为第一天的开始时间）
 * 若 timeStr 为空则使用当前时间作为兜底
 * @param {string} timeStr - "HH:mm:ss" 格式
 * @returns {{ startTime: number, endTime: number }}
 */
function buildEventTimes(timeStr) {
  const start = timeStr
    ? dayjs(`${dayjs().format('YYYY-MM-DD')} ${timeStr}`)
    : dayjs();
  return {
    startTime: Math.floor(start.valueOf() / 1000),
    endTime: Math.floor(start.add(10, 'minute').valueOf() / 1000),
  };
}

/**
 * 检查 APP 版本是否满足最低要求
 * @returns {Promise<boolean>}
 */
async function checkMinVersion() {
  const version = await getVersion();
  if (compareVersions(version, MIN_VERSION) === -1) {
    Toast.show('当前APP版本不支持，请升级到最新版本');
    return false;
  }
  return true;
}

/**
 * 订阅日历提醒 — 创建未来 30 天每日重复提醒
 * @param {string} earliestStartTime - "HH:mm:ss" 格式，第一个任务的开始时间
 */
export async function subscribeRemind(earliestStartTime) {
  if (!env.isEasyBikeApp) return false;
  if (!await checkMinVersion()) return false;

  try {
    const { startTime, endTime } = buildEventTimes(earliestStartTime);
    const res = await addRecurringCalendarEvents({
      title: CALENDAR_REMIND.TITLE,
      startTime,
      endTime,
      days: REMIND_DAYS,
    });

    if (res && res.success) {
      Toast.show(TEXT.TOAST_CALENDAR_ADD_SUCCESS);
      return true;
    }
    Toast.show(res?.message || TEXT.TOAST_CALENDAR_ADD_FAILED);
    return false;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[subscribeRemind] error:', JSON.stringify(error), error);
    Toast.show(TEXT.TOAST_CALENDAR_ADD_FAILED);
    return false;
  }
}

/**
 * 取消日历提醒 — 按 title 批量删除所有同名日程
 */
export async function unsubscribeRemind() {
  if (!env.isEasyBikeApp) return false;
  if (!await checkMinVersion()) return false;

  try {
    const res = await deleteEventsByTitle({
      title: CALENDAR_REMIND.TITLE,
    });

    if (res && res.success) {
      Toast.show(TEXT.TOAST_CALENDAR_DELETE_SUCCESS);
      return true;
    }
    Toast.show(res?.message || TEXT.TOAST_CALENDAR_DELETE_FAILED);
    return false;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[unsubscribeRemind] error:', JSON.stringify(error), error);
    Toast.show(TEXT.TOAST_CALENDAR_DELETE_FAILED);
    return false;
  }
}
